import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { api, asError, setToken, getToken, getDeviceId, adaptiveTimeout } from "@/lib/api";
import { classifyNetError, reconnectStarted, reconnectFailedAgain, reconnectResolved, reconnectAbandoned } from "@/lib/clientIncident";

// Détection d'une coupure (lot 579) : 8 s par essai était trop nerveux sur réseau mobile (3G, perte de signal, démarrage
// à froid) et produisait des « délai dépassé » alors que le serveur était sain — RCA prod du 09/10/2026. Un appareil
// vraiment hors ligne échoue tout de suite (1 essai) ; une passerelle en panne répond 52x vite : les 15 s ne pèsent que
// sur les réseaux lents, ceux qu'on ne veut plus compter comme coupures.
const ME_TIMEOUT_MS = 15000;
const ME_ATTEMPTS = 3;
const ME_RETRY_MS = 1500;
// Lot 713 — délai de /auth/me adapté au lien (2G ×1,6 · 3G ×1,25) quand le garde-fou « net_resilience » est actif.
const meTimeout = () => (guardOn("net_resilience") ? adaptiveTimeout(ME_TIMEOUT_MS) : ME_TIMEOUT_MS);
const CONFIG_FOREGROUND_REFRESH_MS = 10 * 60 * 1000; // lot 684
import { Capacitor } from "@capacitor/core";
import { reportAppVersion } from "@/lib/appVersion";
import { setDefaultImages } from "@/lib/defaults";
import { applyTheme, applyPwaMeta } from "@/lib/theme";
import { initNative, registerNativePush, deregisterNativePush } from "@/lib/nativePush";
import { syncNativeAppLanguage } from "@/lib/nativeAppLanguage";
import { decideLangSync, LANG_KEY } from "@/lib/langSync";
import { toast } from "sonner";
import { syncPushSubscription, detachPushSubscription, detachOrphanPushSubscription } from "@/lib/push";
import { setMediaCdnBase } from "@/components/FileUpload";
import { setIosHlsJs } from "@/lib/videoSource";
import { setLegacyVariants, setImageVariantWidths } from "@/lib/mediaUrl";
import { setGuards, setRollback, guardOn } from "@/lib/guards";
import { isAutomatedBrowser } from "@/lib/bots";
import { readCachedConfig, writeCachedConfig, readCachedConfigEtag, retryDelay } from "@/lib/configCache";
import { bootPrefetch } from "@/lib/bootPrefetch";
import { whenFeedReady } from "@/lib/lowPriority";
import { sharedGet } from "@/lib/sharedGet";
import { readCachedUser, writeCachedUser, clearOfflineCache } from "@/lib/offlineCache";
import { canBootInstantly } from "@/lib/instantBoot";
import { isOffline } from "@/lib/netStatus";
import { resetChatDraftsCache } from "@/lib/chatDrafts";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const LANGUAGES = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
];

const SUPPORTED_LANGS = ["fr", "en", "es", "pt"];

// Preconnect to the video CDNs at startup: the TLS handshake is done before the
// first reel/poster request, shaving ~100-300ms off the very first playback.
const injectCdnPreconnect = (host) => {
  if (!host || typeof document === "undefined") return;
  const href = `https://${String(host).replace(/^https?:\/\//, "").replace(/\/.*$/, "")}`;
  if (document.head.querySelector(`link[data-cdn-preconnect="${href}"]`)) return;
  for (const rel of ["preconnect", "dns-prefetch"]) {
    const l = document.createElement("link");
    l.rel = rel;
    l.href = href;
    if (rel === "preconnect") l.crossOrigin = "anonymous";
    l.setAttribute("data-cdn-preconnect", href);
    document.head.appendChild(l);
  }
};

// First browser-preferred language we actually support (scans the ordered navigator.languages).
// Robot / navigateur automatisé : aucune langue annoncée → langue par défaut (rendu crawler en français).
const deviceLang = () => {
  if (isAutomatedBrowser()) return null;
  const cands = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""]);
  for (const l of cands) {
    const code = (l || "").slice(0, 2).toLowerCase();
    if (SUPPORTED_LANGS.includes(code)) return code;
  }
  return null;
};

// Explicit ?lang= in the URL (legal pages opened from the native app in Safari,
// where localStorage is empty) beats everything else.
const urlLang = () => {
  try {
    const v = new URLSearchParams(window.location.search).get("lang");
    return SUPPORTED_LANGS.includes(v) ? v : null;
  } catch { return null; }
};

const detectBrowserLang = () => {
  const fromUrl = urlLang();
  if (fromUrl) {
    localStorage.setItem("yalola.lang", fromUrl); // page ouverte hors app avec ?lang= : la langue choisie suit sur les pages suivantes
    return fromUrl;
  }
  const stored = localStorage.getItem("yalola.lang");
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  return deviceLang();
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not authed
  const [lang, setLang] = useState(detectBrowserLang() || "fr");
  const [langChosen, setLangChosen] = useState(!!localStorage.getItem("yalola.lang"));
  // Amorce : dernière configuration connue pour cette langue (hors ligne / réseau lent → l'app reste utilisable).
  const [config, setConfig] = useState(() => readCachedConfig(detectBrowserLang() || "fr") || { translations: {}, reactions: [], layout_blocks: [], settings: {}, languages: ["fr", "en", "es", "pt"] });
  const cfgReqRef = useRef(0);
  const cfgRetryRef = useRef(null);
  const cfgLoadedAtRef = useRef(0);
  const langRef = useRef(lang);
  useEffect(() => { langRef.current = lang; }, [lang]);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const reconnectTimerRef = useRef(null);
  const reconnectBusyRef = useRef(false);
  const reconnectNowRef = useRef(null);
  const reconnectCleanupRef = useRef(null);
  const [videoMuted, setVideoMutedState] = useState(() => {
    const v = localStorage.getItem("yalola.videoMuted");
    return v === null ? true : v === "true";
  });
  const [chatTarget, setChatTarget] = useState(null);
  const [storiesUnseen, setStoriesUnseen] = useState(0);
  // opts.draft pre-fills the chat composer (e.g. an ad's headline when opening
  // the conversation from an ad's "Send a message" CTA).
  const openChat = useCallback((u, opts) => setChatTarget(u && (opts?.draft || opts?.ref) ? { ...u, _draft: opts.draft, _ref: opts.ref } : u), []);
  const closeChat = useCallback(() => setChatTarget(null), []);
  const setVideoMuted = useCallback((v) => {
    setVideoMutedState((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      localStorage.setItem("yalola.videoMuted", String(next));
      return next;
    });
  }, []);

  const t = useCallback((key, fallback) => {
    const v = config?.translations?.[key];
    return v || fallback || key;
  }, [config]);

  const applyConfig = useCallback((data) => {
    setMediaCdnBase(data?.storage?.media_cdn_base);
    setLegacyVariants(data?.settings?.image_variants_legacy === true);
    // Lot 679 : tailles de photos publiées par l'admin (864/1080 une fois générées partout) — effet immédiat + mémorisées.
    setImageVariantWidths(data?.settings?.image_variant_widths);
    setIosHlsJs(data?.settings?.reels_ios_hlsjs !== false);
    // Lot 663 : garde-fou marges système + verrou horizontal, désactivable par l'admin sans mise à jour.
    import("@/lib/nativeInsets").then((m) => m.setInsetsGuard(data?.settings?.insets_guard_enabled !== false)).catch(() => {});
    // Lot 671 : garde-fous (son des directs, touches de volume, préchargements, aperçus…) — effet immédiat + mémorisés.
    setGuards(data?.settings?.guards);
    setRollback(data?.settings?.rollback, undefined, data?.settings?.release_safety_enabled !== false);
    injectCdnPreconnect(data?.storage?.video_cdn_host);
    injectCdnPreconnect(data?.storage?.media_cdn_base);
    setConfig(data);
    setDefaultImages(data?.settings?.default_images);
    applyTheme(data?.settings?.brand);
    applyPwaMeta(data?.settings);
  }, []);
  // Config amorcée depuis le cache : thème / images par défaut / CDN appliqués tout de suite.
  const cfgBootRef = useRef(false);
  useEffect(() => {
    if (cfgBootRef.current) return;
    cfgBootRef.current = true;
    if (config?.settings && Object.keys(config.settings).length) applyConfig(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Échec (hors ligne, réseau lent, déploiement) → relances 2 s, 5 s, 10 s, 20 s puis 30 s, et dès le retour du réseau.
  const loadConfig = useCallback(async (l, attempt = 0) => {
    const rid = ++cfgReqRef.current;
    if (cfgRetryRef.current) { clearTimeout(cfgRetryRef.current); cfgRetryRef.current = null; }
    try {
      // Config déjà connue sur l'appareil → If-None-Match : le serveur répond 304 (vide) si rien n'a changé,
      // au lieu de renvoyer ~70 KB (gzip) à chaque ouverture.
      const etag = readCachedConfigEtag(l);
      const res = await api.get(`/config/public?lang=${l}&_t=${Date.now()}`, {
        timeout: 25000,
        headers: etag ? { "If-None-Match": etag } : {},
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
      });
      const data = res.status === 304 ? readCachedConfig(l) : res.data;
      if (!data) throw new Error("config 304 without cache");
      // Guard against a stale-language response (e.g. browser lang differs from
      // account lang) overwriting the latest requested language.
      if (rid !== cfgReqRef.current) return data;
      applyConfig(data);
      cfgLoadedAtRef.current = Date.now();
      if (res.status !== 304) writeCachedConfig(l, data, res.headers?.etag || null);
      return data;
    } catch (e) {
      console.error("config load failed", e);
      if (rid === cfgReqRef.current) cfgRetryRef.current = setTimeout(() => loadConfig(l, attempt + 1), retryDelay(attempt));
      return null;
    }
  }, [applyConfig]);
  useEffect(() => {
    const onOnline = () => { if (cfgRetryRef.current) loadConfig(langRef.current); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [loadConfig]);
  // Lot 684 — réglages distants (garde-fous, mise à jour obligatoire, version store) : jusqu'ici lus seulement au
  // lancement ; une app laissée ouverte des heures ne les voyait jamais. Au retour au premier plan, si la dernière
  // lecture date de plus de 10 min, on relit la config (If-None-Match → 304 vide si rien n'a changé : quasi gratuit).
  useEffect(() => {
    const maybeRefresh = () => { if (Date.now() - cfgLoadedAtRef.current > CONFIG_FOREGROUND_REFRESH_MS) loadConfig(langRef.current); };
    const onVis = () => { if (document.visibilityState === "visible") maybeRefresh(); };
    document.addEventListener("visibilitychange", onVis);
    let sub = null;
    if (Capacitor.isNativePlatform()) {
      import("@capacitor/app").then(({ App }) => App.addListener("appStateChange", ({ isActive }) => { if (isActive) maybeRefresh(); }))
        .then((h) => { sub = h; }).catch(() => {});
    }
    return () => { document.removeEventListener("visibilitychange", onVis); try { sub?.remove?.(); } catch { /* noop */ } };
  }, [loadConfig]);

  const refreshStoriesUnseen = useCallback(async () => {
    if (!getToken()) { setStoriesUnseen(0); return; }
    try {
      const data = await sharedGet("/stories/unseen-count", { ttl: 5000 });
      setStoriesUnseen(Number(data?.count) || 0);
    } catch { /* noop */ }
  }, []);

  // Device choice (yalola.lang) wins and is pushed to the account; no local choice → adopt
  // the account language. Runs after EVERY successful auth so profile, UI and notifications agree.
  const reconcileLang = useCallback((u) => {
    if (!u) return;
    const d = decideLangSync(localStorage.getItem(LANG_KEY), u.language);
    if (d.action === "push") {
      api.patch("/users/me", { language: d.lang })
        .then(({ data }) => { if (data?.user) setUser((prev) => (prev ? { ...prev, ...data.user } : data.user)); })
        .catch(() => {});
    } else if (d.action === "adopt") {
      localStorage.setItem(LANG_KEY, d.lang);
      setLang(d.lang);
      setLangChosen(true);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    if (!getToken()) { setUser(null); detachOrphanPushSubscription(); return null; }
    // The session is ONLY cleared on an explicit 401/403 (token really invalid).
    // Network errors / 5xx (deploy window, offline, server blip) keep the token
    // and retry with backoff — a transient outage must never mass-log-out members.
    const stopReconnect = () => {
      if (reconnectTimerRef.current) { clearInterval(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      reconnectCleanupRef.current?.(); reconnectCleanupRef.current = null; reconnectNowRef.current = null;
      setReconnecting(false);
    };
    // Coupure vue par l'app : causes de chaque échec, puis signalement au serveur une fois reconnecté
    // (lib/clientIncident.js → « Historique des incidents » admin).
    const causes = [];
    const firstTryAt = Date.now();
    for (let attempt = 0; attempt < (isOffline() ? 1 : ME_ATTEMPTS); attempt++) {
      try {
        const { data } = await api.get("/auth/me", { timeout: meTimeout() });
        if (data.token) setToken(data.token); // session glissante : jeton prolongé par le serveur
        setUser(data.user);
        writeCachedUser(data.user);
        stopReconnect();
        reconnectResolved();
        reportAppVersion();
        reconcileLang(data.user);
        return data.user;
      } catch (e) {
        const st = e?.response?.status;
        if (st === 401 || st === 403) {
          setUser(null);
          clearOfflineCache();
          setToken(null);
          stopReconnect();
          reconnectAbandoned();
          detachOrphanPushSubscription();
          return null;
        }
        causes.push(classifyNetError(e));
        if (attempt < ME_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, ME_RETRY_MS * (attempt + 1)));
      }
    }
    // Server unreachable but a session exists: RECONNECT MODE — keep the token and retry in the
    // background until the server answers (WhatsApp-style). Session déjà connue sur l'appareil →
    // MODE HORS LIGNE : l'app reste consultable (dernier fil lu) avec un bandeau discret ; sinon écran plein.
    setUser(readCachedUser());
    setReconnecting(true);
    reconnectStarted(causes, firstTryAt);
    if (!reconnectTimerRef.current) {
      const attemptReconnect = async () => {
        if (reconnectBusyRef.current) return;
        reconnectBusyRef.current = true;
        try {
          const { data } = await api.get("/auth/me", { timeout: meTimeout() });
          if (data.token) setToken(data.token);
          stopReconnect();
          reconnectResolved();
          reportAppVersion();
          setUser(data.user);
          writeCachedUser(data.user);
          reconcileLang(data.user);
          loadConfig(langRef.current); // textes / réglages modifiés pendant la coupure
          window.dispatchEvent(new Event("yalola:reconnected"));
        } catch (e2) {
          const st2 = e2?.response?.status;
          if (st2 === 401 || st2 === 403) {
            setToken(null);
            setUser(null);
            clearOfflineCache();
            stopReconnect();
            reconnectAbandoned();
          } else {
            reconnectFailedAgain(classifyNetError(e2));
          }
        } finally { reconnectBusyRef.current = false; }
      };
      // Toutes les 5 s + IMMÉDIATEMENT au retour du réseau (événement navigateur / Capacitor), de l'app au premier plan
      // ou sur « Réessayer maintenant ».
      reconnectTimerRef.current = setInterval(attemptReconnect, 5000);
      const onBack = () => { if (reconnectTimerRef.current) attemptReconnect(); };
      const onVis = () => { if (document.visibilityState === "visible") onBack(); };
      window.addEventListener("online", onBack);
      window.addEventListener("yalola:network-online", onBack);
      document.addEventListener("visibilitychange", onVis);
      reconnectNowRef.current = onBack;
      reconnectCleanupRef.current = () => {
        window.removeEventListener("online", onBack);
        window.removeEventListener("yalola:network-online", onBack);
        document.removeEventListener("visibilitychange", onVis);
      };
    }
    return null;
  }, [reconcileLang, loadConfig]);
  const reconnectNow = useCallback(() => { reconnectNowRef.current ? reconnectNowRef.current() : refreshMe().catch(() => {}); }, [refreshMe]);

  useEffect(() => {
    (async () => {
      // Session connue → le fil (ou les Reels) et les stories partent TOUT DE SUITE, en parallèle de config/me.
      if (getToken()) {
        const cachedUser = readCachedUser();
        const path = window.location.pathname;
        if (path.startsWith("/reels")) {
          bootPrefetch("/reels", { lang: (cachedUser?.language || lang || "fr").toLowerCase().slice(0, 2) });
        } else if (path === "/" || path === "/home") {
          const ml = (config?.settings?.features?.multilingual_discovery) !== false;
          bootPrefetch("/posts", { limit: 15, ...(ml ? { lang: (cachedUser?.language || "fr").toLowerCase().slice(0, 2) } : {}) });
          bootPrefetch("/stories");
        }
        // Lot 716 — démarrage instantané : session + réglages mémorisés → l'app s'affiche sans attendre le serveur
        // (Home amorce le fil depuis le cache) ; /auth/me confirme ou renvoie à la connexion (401) juste après.
        if (canBootInstantly({ token: true, cachedUser, config })) { setUser(cachedUser); setLoading(false); }
      }
      const [cfg, me] = await Promise.all([loadConfig(lang), refreshMe()]);
      initNative();
      if (me) { registerNativePush(cfg?.settings); syncPushSubscription(); }
      // Apply the admin's default-language policy only for anonymous visitors who
      // haven't explicitly chosen a language yet.
      if (!localStorage.getItem("yalola.lang") && !me && cfg?.settings && !urlLang()) {
        const def = SUPPORTED_LANGS.includes(cfg.settings.default_language) ? cfg.settings.default_language : "fr";
        let target = def;
        if (cfg.settings.detect_device_language !== false) {
          target = deviceLang() || def;
        }
        if (target !== lang) setLang(target);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstLangLoad = useRef(true);
  useEffect(() => {
    if (firstLangLoad.current) { firstLangLoad.current = false; return; }
    loadConfig(lang);
  }, [lang, loadConfig]);

  // Keep <html lang> in sync with the active UI language (a11y + stops the
  // browser's "translate this page?" prompt when the UI already matches).
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
    syncNativeAppLanguage(lang);
  }, [lang]);

  // Native only: configure RevenueCat (App Store / Google Play) once the member and the store keys are known.
  useEffect(() => {
    if (!user?.id || !config?.payments) return;
    import("@/lib/iap").then((m) => m.configureIap(config.payments, user.id)).catch(() => {});
  }, [user?.id, config?.payments]);

  // Back to the foreground (native app / tab) → refresh the account (identity_status, badge…) so a
  // decision taken while the app was in the background shows up without a manual reload.
  const lastMeRef = useRef(0);
  useEffect(() => {
    if (!user) return undefined;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastMeRef.current < 30000) return;
      lastMeRef.current = now;
      refreshMe().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [user, refreshMe]);

  // First-party analytics heartbeat (online count, visits, session duration)
  useEffect(() => {
    let sid = sessionStorage.getItem("yalola.sid");
    if (!sid) { sid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())); sessionStorage.setItem("yalola.sid", sid); }
    let vid = localStorage.getItem("yalola.vid");
    if (!vid) { vid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())); localStorage.setItem("yalola.vid", vid); }
    const beat = () => api.post("/analytics/heartbeat", { session_id: sid, visitor_id: vid }).catch(() => {});
    const cancelFirst = whenFeedReady(beat); // 1er battement après le 1er rendu du fil (priorité au contenu)
    const stop = pausableInterval(beat, 60000, { onResume: false }); // lot 699 — pas de battement « en ligne » app cachée
    return () => { stop(); cancelFirst(); };
  }, []);

  // Unseen-stories badge on the feed icon — polled + refreshed on story activity.
  useEffect(() => {
    if (!user) { setStoriesUnseen(0); return; }
    refreshStoriesUnseen();
    const stop = pausableInterval(refreshStoriesUnseen, 60000); // lot 699 — repos en arrière-plan, rafraîchi au retour
    const onUpdate = () => refreshStoriesUnseen();
    window.addEventListener("yalola:stories_updated", onUpdate);
    window.addEventListener("yalola:story_created", onUpdate);
    return () => {
      stop();
      window.removeEventListener("yalola:stories_updated", onUpdate);
      window.removeEventListener("yalola:story_created", onUpdate);
    };
  }, [user, refreshStoriesUnseen]);

  const chooseLang = (code) => {
    localStorage.setItem("yalola.lang", code);
    setLang(code);
    setLangChosen(true);
    // iOS: system menus (Paste/AutoFill, alerts) follow at the next cold launch.
    syncNativeAppLanguage(code).then((changed) => {
      if (changed) toast(t("settings.ios_lang_next_launch", "Les menus iOS passeront dans cette langue au prochain démarrage de l'app."));
    });
    // Persist the choice to the account so it follows the user across devices.
    if (getToken()) {
      api.patch("/users/me", { language: code })
        .then(({ data }) => { if (data?.user) setUser((u) => (u ? { ...u, ...data.user } : data.user)); })
        .catch(() => {});
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password, device_id: getDeviceId(), language: lang });
    // New/untrusted device → backend asks for a step-up OTP before issuing the session.
    if (data.requires_device_verification) {
      return {
        requiresDeviceVerification: true,
        channel: data.channel,
        hint: data.hint,
        email: data.email,
        phone: data.phone,
        dev_code: data.dev_code,
      };
    }
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };

  // WhatsApp (passwordless) registration + login, and new-device step-up verification.
  const startPhoneRegistration = async (payload) =>
    (await api.post("/auth/register/phone/start", { ...payload, device_id: getDeviceId() })).data;
  const resendPhoneRegistration = async (phone) =>
    (await api.post("/auth/register/phone/resend", { phone, language: lang })).data;
  const verifyPhoneRegistration = async (phone, code) => {
    const { data } = await api.post("/auth/register/phone/verify", { phone, code, device_id: getDeviceId() });
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };
  const startPhoneLogin = async (phone) =>
    (await api.post("/auth/login/phone/start", { phone, language: lang })).data;
  const verifyPhoneLogin = async (phone, code) => {
    const { data } = await api.post("/auth/login/phone/verify", { phone, code, device_id: getDeviceId() });
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };
  const verifyDevice = async ({ email, phone, code }) => {
    const { data } = await api.post("/auth/device/verify", { email, phone, code, device_id: getDeviceId() });
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };
  // Lot 563 — connexion par code e-mail (sans mot de passe), symétrique de la connexion WhatsApp.
  const startEmailLogin = async (email) =>
    (await api.post("/auth/login/email/start", { email, language: lang })).data;
  const verifyEmailLogin = async (email, code) => {
    const { data } = await api.post("/auth/login/email/verify", { email, code, device_id: getDeviceId() });
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };

  // Email OTP registration
  const startRegistration = async (payload) => (await api.post("/auth/register/start", payload)).data;
  const resendRegistration = async (email) => (await api.post("/auth/register/resend", { email })).data;
  const verifyRegistration = async (email, code) => {
    const { data } = await api.post("/auth/register/verify", { email, code });
    if (data.token) setToken(data.token);
    setUser(data.user);
    registerNativePush();
    reconcileLang(data.user);
    return data.user;
  };

  // Forgot password (6-digit code → reset token → new password)
  const forgotPassword = async (email, extra = {}) => (await api.post("/auth/forgot-password", { email, ...extra })).data;
  const resendForgot = async (email) => (await api.post("/auth/forgot-password/resend", { email })).data;
  const verifyForgot = async (email, code) => (await api.post("/auth/forgot-password/verify", { email, code })).data;
  const resetPassword = async (email, reset_token, new_password) =>
    (await api.post("/auth/reset-password", { email, reset_token, new_password })).data;

  const logout = async () => {
    // Detach ONLY this browser / this device from push before the token is dropped.
    await Promise.all([detachPushSubscription(), deregisterNativePush()]);
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(null);
    clearOfflineCache();
    resetChatDraftsCache();
  };

  const value = {
    user,
    setUser,
    lang,
    setLang: chooseLang,
    langChosen,
    setLangChosen,
    config,
    setConfig,
    t,
    loading,
    reconnecting,
    reconnectNow,
    videoMuted,
    setVideoMuted,
    login,
    register,
    startRegistration,
    resendRegistration,
    verifyRegistration,
    startPhoneRegistration,
    resendPhoneRegistration,
    verifyPhoneRegistration,
    startPhoneLogin,
    verifyPhoneLogin,
    startEmailLogin,
    verifyEmailLogin,
    verifyDevice,
    forgotPassword,
    resendForgot,
    verifyForgot,
    resetPassword,
    logout,
    refreshMe,
    asError,
    chatTarget,
    openChat,
    closeChat,
    storiesUnseen,
    refreshStoriesUnseen,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
